import type { ReactNode } from "react";
import Link from "next/link";
import SmartCtaLink, { type CtaAction } from "./SmartCtaLink";

export type PageHeroAction = CtaAction;

export type PageHeroChip = {
  label: string;
  href?: string;
};

/**
 * Компактная шапка страницы: слева заголовок с описанием, бейджами и
 * быстрыми якорями, справа — пара CTA. Слот children идёт строго под сеткой
 * и используется под табы, цифры или фильтры. Server component: строки
 * приходят пропсами, внутри нет ни хардкода, ни 'use client'.
 */
export default function PageHero({
  id,
  title,
  description,
  badges,
  chips,
  actions,
  children,
}: {
  id?: string;
  title: string;
  description?: string;
  /** Информационные пилюли без иконок: сроки, формат, SLA. */
  badges?: string[];
  /** Кликабельные якоря-навигация по странице. */
  chips?: PageHeroChip[];
  actions?: PageHeroAction[];
  children?: ReactNode;
}) {
  const titleId = id ? `${id}-title` : undefined;

  return (
    <header id={id} aria-labelledby={titleId} className="mt-2 mb-6 md:mt-4 md:mb-8">
      <div className="flex flex-col gap-4 text-left lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <h1
            id={titleId}
            className="text-2xl font-bold tracking-tight text-white md:text-3xl lg:text-4xl"
          >
            {title}
          </h1>
          {description ? (
            <p className="mt-2 text-sm leading-relaxed text-white/70">{description}</p>
          ) : null}
          {badges && badges.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {badges.map((badge) => (
                <span
                  key={badge}
                  className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/60"
                >
                  {badge}
                </span>
              ))}
            </div>
          ) : null}
          {chips && chips.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {chips.map((chip) =>
                chip.href ? (
                  <Link
                    key={chip.label}
                    href={chip.href}
                    className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:border-white/35 hover:text-white"
                  >
                    {chip.label}
                  </Link>
                ) : (
                  <span
                    key={chip.label}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs text-white/60"
                  >
                    {chip.label}
                  </span>
                )
              )}
            </div>
          ) : null}
        </div>
        {actions && actions.length > 0 ? (
          <div className="flex flex-wrap gap-3 lg:shrink-0 lg:pt-1">
            {actions.map((action) => (
              <SmartCtaLink key={`${action.href}-${action.label}`} {...action} />
            ))}
          </div>
        ) : null}
      </div>
      {children ? <div className="mt-6 md:mt-8">{children}</div> : null}
    </header>
  );
}
